import * as apicall from "../components/Apicall";
import { apiUrl } from "../constants/Constants";
import { qs } from "./platformService";

// The supplier portal's API client (/api/v1/proc/supplier). Used only by signed-in suppliers (role 8);
// the API refuses a supplier token anywhere else and any other role here.

export const getMe = () => apicall.get("proc/supplier/me");
export const getCompany = () => apicall.get("proc/supplier/company");
export const saveCompany = (data) => apicall.post(data, "proc/supplier/company");
export const getBuyers = () => apicall.get("proc/supplier/buyers");

export const getDocuments = () => apicall.get("proc/supplier/documents");
// fields: { document_type_code, expiry_date? }, file: a File
export const uploadDocument = ({ document_type_code, expiry_date }, file) => {
  const form = new FormData();
  form.append("document_type_code", document_type_code);
  if (expiry_date) form.append("expiry_date", expiry_date);
  form.append("file", file);
  return apicall.post(form, "proc/supplier/documents", true);
};
export const getDocumentUrl = (id) => apicall.get(`proc/supplier/documents/${id}/url`);

export const getUsers = () => apicall.get("proc/supplier/users");
export const addUser = (data) => apicall.post(data, "proc/supplier/users"); // { first_name, email, phone }
export const setUserStatus = (userId, status) => apicall.post({ status }, `proc/supplier/users/${userId}/status`);

export const getInvitations = () => apicall.get("proc/supplier/invitations");
// ref: { invitation_id } (picked from the list) or { token } (from the email link)
export const acceptInvitation = (ref) => apicall.post(ref, "proc/supplier/invitations/accept");

/* ---------------- the invitation link (no login yet) ---------------- */
// Plain fetches: there is no token to send, and a refused answer must not log anyone out.
async function publicCall(method, path, body) {
  const res = await fetch(`${apiUrl}api/v1/proc/public/${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json().catch(() => ({ success: false, message: "Something went wrong" }));
}
export const describeInvite = (token) => publicCall("GET", `invites/${encodeURIComponent(token)}`);
export const acceptInvite = (token, data) => publicCall("POST", `invites/${encodeURIComponent(token)}/accept`, data);

/* ---------------- RFQs and quotations (P2) ---------------- */
export const getRfqSummary = () => apicall.get("proc/supplier/rfqs/summary");
export const listRfqs = (params = {}) => apicall.get(`proc/supplier/rfqs${qs(params)}`); // { buyer_id, status: "OPEN,QUOTED", q }
export const getRfq = (id) => apicall.get(`proc/supplier/rfqs/${id}`);
export const declineRfq = (id, reason) => apicall.post({ reason }, `proc/supplier/rfqs/${id}/decline`);
export const requestRfqInfo = (id, note) => apicall.post({ note }, `proc/supplier/rfqs/${id}/request-info`);
// data: { delivery_time, quotation_validity, payment_terms, additional_info, lines: [{ line_id, price, quantity?, remarks? }] }
export const sendQuotation = (id, data) => apicall.post(data, `proc/supplier/rfqs/${id}/quotation`);
export const uploadQuotationDocument = (id, needId, file) => {
  const form = new FormData();
  form.append("need_id", needId);
  form.append("file", file);
  return apicall.post(form, `proc/supplier/rfqs/${id}/documents`, true);
};
export const removeQuotationDocument = (id, responseId) => apicall.del(`proc/supplier/rfqs/${id}/documents/${responseId}`);
export const getQuotationDocumentUrl = (id, responseId) => apicall.get(`proc/supplier/rfqs/${id}/documents/${responseId}/url`);
export const getRfqAttachmentUrl = (id, fileId) => apicall.get(`proc/supplier/rfqs/${id}/attachments/${fileId}/url`);

/* ---------------- messages (P3) ---------------- */
export const listThreads = (params = {}) => apicall.get(`proc/supplier/messages/threads${qs(params)}`); // { rfq_id }
export const getThread = (ref) => apicall.get(`proc/supplier/messages/thread${qs(ref)}`); // { buyer_id, rfq_id? }
export const getUnreadMessages = () => apicall.get("proc/supplier/messages/unread");
// ref: { buyer_id, rfq_id? }. A file makes it a multipart request.
export const sendMessage = (ref, message, file) => {
  if (!file) return apicall.post({ ...ref, message }, "proc/supplier/messages");
  const form = new FormData();
  Object.entries(ref).forEach(([k, v]) => v && form.append(k, v));
  if (message) form.append("message", message);
  form.append("file", file);
  return apicall.post(form, "proc/supplier/messages", true);
};
export const getMessageFileUrl = (id) => apicall.get(`proc/supplier/messages/files/${id}/url`);

/* ---------------- orders (P4) ---------------- */
export const listOrders = (params = {}) => apicall.get(`proc/supplier/orders${qs(params)}`); // { status }
export const getOrder = (id) => apicall.get(`proc/supplier/orders/${id}`);
export const acceptOrder = (id) => apicall.post({}, `proc/supplier/orders/${id}/accept`);
export const rejectOrder = (id, reason) => apicall.post({ reason }, `proc/supplier/orders/${id}/reject`);
export const shipOrder = (id, status, note) => apicall.post({ status, note }, `proc/supplier/orders/${id}/ship`); // status: SHIPPED | OUT_FOR_DELIVERY
export const setOrderDeliveryDate = (id, date) => apicall.post({ delivery_date: date }, `proc/supplier/orders/${id}/delivery-date`);
// fields: { invoice_number, invoice_date, amount }, file: a File
export const sendInvoice = (id, { invoice_number, invoice_date, amount }, file) => {
  const form = new FormData();
  form.append("invoice_number", invoice_number);
  form.append("invoice_date", invoice_date);
  form.append("amount", amount);
  form.append("file", file);
  return apicall.post(form, `proc/supplier/orders/${id}/invoice`, true);
};
export const getInvoiceUrl = (invoiceId) => apicall.get(`proc/supplier/orders/invoices/${invoiceId}/url`);

export const getDashboard = () => apicall.get("proc/supplier/dashboard");
